// material-ui
import CloseIcon from '@mui/icons-material/Close';
import { LibraryMusicOutlined } from '@mui/icons-material';
import {
    AppBar,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppBreadcrumbsNavigation from 'ui-component/extended/AppBreadcrumbsNavigation';
import { selectCustomersidname } from '../../../store/customers/customersSlice';
import DialogAddAgent from '../../../ui-component/dialogs/addAgentDialog';
import DialogAddDoc from '../../../ui-component/dialogs/addDocDialog';
import DialogSearchDoc from '../../../ui-component/dialogs/searchDocDialog';
import { subStringMatch } from '../../../utils/sort';
import { Transition } from '../../../views/customer/CustomerList';
import CustomerPreview from '../../../views/customer/profile/documents/CustomerPreview';
import LogoSection from '../LogoSection';
import { AutoCompleteInput } from './AutoCompleteInput';
import { MenuToggleButton } from './MenuToggleButton';
import MobileSection from './MobileSection';
import { PopperMy } from './PopperMy';
import ProfileSection from './ProfileSection';
import DocsBadge from './ordersBadge/docsBadge';
import InventoryBadge from './ordersBadge/inventoryBadge';
import OrdersBadge from './ordersBadge/ordersBadge';
import OtherAppBadge from './ordersBadge/OtherAppBadge';
import ConnectionIndicators from './ConnectionIndicators';
import NotificationSection from './NotificationSection';
import JSDB from '../../../fromKotlin/nk';
import { dateObjectToString, roundDecimals, StringToIsrael } from '../../../fromKotlin/nkutils';
import type { TaklitProduct } from '../../../api/customer/taklit.api';
import { calculateTaklit, fetchTaklitProducts } from '../../../api/customer/taklit.api';
import Swal from 'sweetalert2';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

export interface HeaderProps {
    leftDrawerOpened: boolean;
    handleLeftDrawerToggle: () => void;
}

const Header = React.memo(({ leftDrawerOpened, handleLeftDrawerToggle }: HeaderProps) => {
    const theme = useTheme();
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [curCustomer, setCustomer] = React.useState<any>(undefined);
    const customers: any[] = useSelector(selectCustomersidname);
    const taklitAllowed = JSDB().getUser().first?.taklit === 1;
    const [taklitOpen, setTaklitOpen] = useState(false);
    const [taklitDate, setTaklitDate] = useState(() => dateObjectToString(new Date()));
    const [taklitRaw, setTaklitRaw] = useState<TaklitProduct[]>([]);
    const [taklitLoading, setTaklitLoading] = useState(false);
    const [taklitRecalcLoading, setTaklitRecalcLoading] = useState(false);
    const [taklitSearch, setTaklitSearch] = useState('');

    const loadTaklit = useCallback(async () => {
        setTaklitLoading(true);
        try {
            const list = await fetchTaklitProducts(taklitDate);
            setTaklitRaw(list);
        } catch {
            void Swal.fire({ icon: 'error', title: 'שגיאה', text: 'טעינת תקליט נכשלה' });
        } finally {
            setTaklitLoading(false);
        }
    }, [taklitDate]);

    useEffect(() => {
        if (!taklitOpen || !taklitAllowed) return;
        void loadTaklit();
    }, [taklitOpen, taklitAllowed, loadTaklit]);

    useEffect(() => {
        if (!taklitAllowed) setTaklitOpen(false);
    }, [taklitAllowed]);

    const taklitRows = useMemo(() => {
        const byDate = taklitRaw.filter((r) => r.date === taklitDate);
        const q = taklitSearch.trim().toLowerCase();
        const searched = q ? byDate.filter((r) => r.name.toLowerCase().includes(q)) : byDate;
        return [...searched].sort((a, b) => a.name.localeCompare(b.name, 'he'));
    }, [taklitRaw, taklitDate, taklitSearch]);

    const taklitLastUpdate = useMemo(() => {
        if (taklitRaw.length === 0) return '';
        const max = taklitRaw.reduce((m, r) => (r.date > m ? r.date : m), taklitRaw[0].date);
        return StringToIsrael(max, false);
    }, [taklitRaw]);

    const onRecalculateTaklit = useCallback(async () => {
        setTaklitRecalcLoading(true);
        try {
            const ok = await calculateTaklit();
            if (!ok) {
                void Swal.fire({ icon: 'error', title: 'שגיאה', text: 'חישוב תקליט נכשל' });
                return;
            }
            await loadTaklit();
        } catch {
            void Swal.fire({ icon: 'error', title: 'שגיאה', text: 'חישוב תקליט נכשל' });
        } finally {
            setTaklitRecalcLoading(false);
        }
    }, [loadTaklit]);

    const handleClose = useCallback(() => {
        setPreviewOpen(false);
    }, []);

    const onAutoCompleteChange = useCallback((event, value) => {
        if (value) {
            setPreviewOpen(true);
            setCustomer(value);
        }
    }, []);

    const handleBackdropClick = useCallback(() => setPreviewOpen(false), []);

    const externalIdSearch = parseInt(localStorage.getItem('externalIdOrders') ?? '0') === 1;

    const filterOptions = useCallback((options, { inputValue }) => {
        return options
            .filter((option) => {
                return subStringMatch(inputValue, [option.name, externalIdSearch ? option.externalId : option.id]);
            })
            .sort((a, b) => {
                if (inputValue == '') {
                    return a.position > b.position ? 1 : -1;
                } else {
                    const n1 = externalIdSearch ? a.externalId : a.id;
                    const n2 = externalIdSearch ? b.externalId : b.id;

                    if (Number(n1) && Number(n2)) {
                        return Number(n1) > Number(n2) ? 1 : -1;
                    } else if (Number(n1)) {
                        return 1;
                    } else if (Number(n2)) {
                        return -1;
                    } else return n1 > n2 ? 1 : -1;
                }
            });
    }, [externalIdSearch]);

    const getOptionLabel = useCallback((o) => `${externalIdSearch ? o.externalId : o.id} - ${o.name}`, [externalIdSearch]);

    return (
        <Grid container>
            <Box
                sx={{
                    width: 228,
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>
                <MenuToggleButton leftDrawerOpened={leftDrawerOpened} handleLeftDrawerToggle={handleLeftDrawerToggle} />
            </Box>
            <Box
                sx={{
                    ml: 3,
                    '& .rtl-1q9lxz1': {
                        padding: '6px 8px 6px 8px'
                    }
                }}
            >
                <AppBreadcrumbsNavigation />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box
                sx={{
                    marginRight: '70px'
                }}
            >
                <OtherAppBadge />
                <OrdersBadge />
                <DocsBadge />
                <InventoryBadge />
                {taklitAllowed ? (
                    <Tooltip title="תקליט — מחירון" placement="bottom">
                        <Chip
                            icon={<LibraryMusicOutlined />}
                            label="תקליט"
                            onClick={() => setTaklitOpen(true)}
                            variant="outlined"
                            sx={{
                                ml: 0.5,
                                mr: 0.5,
                                verticalAlign: 'middle',
                                borderRadius: '5px',
                                border: '1px solid #000080',
                                fontWeight: 'bold',
                                '& .MuiChip-icon': { color: '#000080' },
                            }}
                        />
                    </Tooltip>
                ) : null}
            </Box>

            {/* Connection Status Indicators */}
            <ConnectionIndicators />
            {/* AI assistant inbox: scheduled task results / reminders */}
            <NotificationSection />

            <DialogAddAgent />
            <DialogAddDoc />
            <DialogSearchDoc />
            <Autocomplete
                onChange={onAutoCompleteChange}
                PopperComponent={PopperMy}
                options={customers}
                getOptionLabel={getOptionLabel}
                filterOptions={filterOptions}
                size="small"
                autoHighlight
                renderInput={(params) => AutoCompleteInput(params)}
            />

            {previewOpen && curCustomer ? (
                <Dialog
                    TransitionComponent={Transition}
                    fullScreen
                    fullWidth
                    open={previewOpen}
                    onBackdropClick={handleBackdropClick}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <AppBar sx={{ position: 'sticky' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1, color: 'white' }} variant="h6" component="div">
                                סגור חלון
                            </Typography>
                            <Typography component="span" sx={{ cursor: 'pointer' }} color="inherit" onClick={handleClose}>
                                {curCustomer.name}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <CustomerPreview id={curCustomer.id} />
                </Dialog>
            ) : undefined}
            {taklitAllowed ? (
                <Dialog open={taklitOpen} onClose={() => setTaklitOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
                        תקליט
                        {taklitLastUpdate ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                                עדכון אחרון: {taklitLastUpdate}
                            </Typography>
                        ) : null}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                            <TextField
                                label="תאריך"
                                type="date"
                                size="small"
                                value={taklitDate}
                                onChange={(e) => setTaklitDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="חיפוש לפי שם"
                                size="small"
                                value={taklitSearch}
                                onChange={(e) => setTaklitSearch(e.target.value)}
                                sx={{ minWidth: 200, flex: 1 }}
                            />
                            <Button variant="outlined" onClick={() => void loadTaklit()} disabled={taklitLoading}>
                                רענן
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => void onRecalculateTaklit()}
                                disabled={taklitRecalcLoading || taklitLoading}
                                startIcon={taklitRecalcLoading ? <CircularProgress color="inherit" size={18} /> : undefined}
                            >
                                חשב מחדש
                            </Button>
                        </Box>
                        {taklitLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : taklitRows.length === 0 ? (
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                                אין רשומות תקליט לתאריך זה.
                            </Typography>
                        ) : (
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>מזהה</TableCell>
                                        <TableCell>שם</TableCell>
                                        <TableCell align="right">מחיר א׳</TableCell>
                                        <TableCell align="right">מחיר ב׳</TableCell>
                                        <TableCell>תאריך</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {taklitRows.map((row) => (
                                        <TableRow key={`${row.id}-${row.date}`}>
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell align="right">{roundDecimals(row.pricea, 4)}</TableCell>
                                            <TableCell align="right">{roundDecimals(row.priceb, 4)}</TableCell>
                                            <TableCell>{row.date ? StringToIsrael(row.date, false) : ''}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setTaklitOpen(false)}>סגור</Button>
                    </DialogActions>
                </Dialog>
            ) : null}
            <div style={{ width: '55px' }}></div>
            <ProfileSection />
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <MobileSection />
            </Box>
        </Grid>
    );
});

export default Header;
